/**
 * Sentry Configuration and Error Monitoring
 * Provides comprehensive error tracking and performance monitoring
 */

import * as Sentry from '@sentry/react';

interface SentryConfig {
  dsn?: string;
  environment?: string;
  release?: string;
  tracesSampleRate?: number;
  profilesSampleRate?: number;
}

/**
 * Initialize Sentry for error monitoring and performance tracking
 */
export function initializeSentry(config?: SentryConfig): void {
  const isDevelopment = import.meta.env.DEV;
  const isProduction = import.meta.env.PROD;

  // Skip initialization in development unless explicitly enabled
  if (isDevelopment && !import.meta.env.VITE_SENTRY_DEBUG) {
    console.log('🐛 Sentry disabled in development mode');
    return;
  }

  const sentryConfig: Sentry.BrowserOptions = {
    dsn: config?.dsn || import.meta.env.VITE_SENTRY_DSN,
    environment: config?.environment || import.meta.env.VITE_ENVIRONMENT || 'development',
    release: config?.release || import.meta.env.VITE_SENTRY_RELEASE || 'development',

    // Performance Monitoring
    integrations: [Sentry.browserTracingIntegration()],

    // Performance monitoring sample rates
    tracesSampleRate: config?.tracesSampleRate || (isProduction ? 0.1 : 1.0),
    profilesSampleRate: config?.profilesSampleRate || (isProduction ? 0.1 : 1.0),

    // Error filtering and privacy
    beforeSend(event) {
      // Filter out development errors
      if (isDevelopment && event.exception) {
        const error = event.exception.values?.[0];
        if (error?.value?.includes('ResizeObserver loop limit exceeded')) {
          return null; // Common React development warning
        }
      }

      // Remove sensitive data
      if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
      }

      return event;
    },

    // Configure which data to capture
    sendDefaultPii: false, // Don't send personally identifiable information
    attachStacktrace: true,
    debug: isDevelopment,

    // Error boundary configuration
    autoSessionTracking: true,
    sendClientReports: isProduction,

    // Custom tags for filtering
    initialScope: {
      tags: {
        component: 'web-app',
        framework: 'react',
        buildTool: 'vite',
      },
      contexts: {
        browser: {
          name: navigator.userAgent,
        },
      },
    },
  };

  Sentry.init(sentryConfig);

  // Add global error handler for uncaught promises
  window.addEventListener('unhandledrejection', event => {
    Sentry.captureException(event.reason);
  });

  console.log(`🔍 Sentry initialized for ${sentryConfig.environment} environment`);
}

/**
 * Error Boundary HOC for React components
 */
export const SentryErrorBoundary = Sentry.withErrorBoundary;

/**
 * Performance monitoring utilities
 */
export const Performance = {
  /**
   * Start a performance span
   */
  startSpan: <T>(name: string, operation: string | undefined, callback: () => T): T => {
    return Sentry.startSpan(
      {
        name,
        op: operation || 'navigation',
      },
      callback,
    );
  },

  /**
   * Measure component render time
   */
  measureComponentRender: <T>(componentName: string, callback: () => T): T => {
    return Sentry.startSpan(
      {
        name: `Component: ${componentName}`,
        op: 'react.render',
      },
      callback,
    );
  },

  /**
   * Track API call performance
   */
  trackApiCall: <T>(url: string, method: string = 'GET', callback: () => T): T => {
    return Sentry.startSpan(
      {
        name: `API: ${method} ${url}`,
        op: 'http.client',
      },
      callback,
    );
  },
};

/**
 * Custom error reporting utilities
 */
export const ErrorReporting = {
  /**
   * Capture a handled error with context
   */
  captureError: (error: Error, context?: Record<string, any>) => {
    Sentry.withScope(scope => {
      if (context) {
        scope.setContext('errorContext', context);
      }
      Sentry.captureException(error);
    });
  },

  /**
   * Capture a message with severity level
   */
  captureMessage: (message: string, level: Sentry.SeverityLevel = 'info') => {
    Sentry.captureMessage(message, level);
  },

  /**
   * Add breadcrumb for debugging
   */
  addBreadcrumb: (message: string, category?: string, level?: Sentry.SeverityLevel) => {
    Sentry.addBreadcrumb({
      message,
      category: category || 'app',
      level: level || 'info',
      timestamp: Date.now() / 1000,
    });
  },

  /**
   * Set user context for error tracking
   */
  setUser: (user: { id: string; username?: string }) => {
    Sentry.setUser({
      id: user.id,
      username: user.username,
    });
  },

  /**
   * Clear user context (on logout)
   */
  clearUser: () => {
    Sentry.setUser(null);
  },
};

/**
 * Firebase integration helpers
 */
export const FirebaseIntegration = {
  /**
   * Track Firebase operation performance
   */
  trackFirebaseOperation: <T>(operation: string, callback: () => T): T => {
    return Sentry.startSpan(
      {
        name: `Firebase: ${operation}`,
        op: 'firebase.operation',
      },
      callback,
    );
  },

  /**
   * Handle Firebase errors
   */
  handleFirebaseError: (error: any, operation: string) => {
    Sentry.withScope(scope => {
      scope.setTag('firebase.operation', operation);
      scope.setContext('firebase', {
        code: error.code,
        message: error.message,
        operation,
      });
      Sentry.captureException(error);
    });
  },
};

// Export Sentry directly for advanced usage
export { Sentry };
